import { createContext, useCallback, useReducer } from "react";
import useAuth from "../../hooks/useAuth";

import axios from "../../utils/axios/v1/userAxios";
import { ConfigAction } from "../../utils/constants";

const GET_ZONES = "GET_ZONES";
const GET_ZONES_FOR_ITEMS = "GET_ZONES_FOR_ITEMS";
const GET_ZONES_FOR_LIST = "GET_ZONES_FOR_LIST";
const CONFIG_ZONE = "CONFIG_ZONE";
const GET_REGIONAL_DETECTIONS = "GET_REGIONAL_DETECTIONS";
const GET_TOP_SOURCE = "GET_TOP_SOURCE";
const GET_TOP_REGION = "GET_TOP_REGION";
const GET_TOP_PATH = "GET_TOP_PATH";
const GET_TOP_UA = "GET_TOP_UA";
const GET_TOP_PROMPT_SCANNERS = "GET_TOP_PROMPT_SCANNERS";
const GET_TOP_OUTPUT_SCANNERS = "GET_TOP_OUTPUT_SCANNERS";
const GET_TOP_METHOD = "GET_TOP_METHOD";
const GET_TOP_RESPONSE_CODE = "GET_TOP_RESPONSE_CODE";
const GET_LLM_SIGS = "GET_LLM_SIGS";
const GET_EVENTS = "GET_EVENTS";
const GET_TOTAL_EVENTS_COUNT = "GET_TOTAL_EVENTS_COUNT";
const SET_CURRENT_STATUS = "SET_CURRENT_STATUS";
const SET_ERROR = "SET_ERROR";

const initialState = {
  zones: null,
  zoneList: null,
  zone_List: null,
  curzone: null,
  regional_detections: null,
  top_source_detection: null,
  top_path: null,
  top_ua: null,
  top_prompt_scanners: null,
  top_output_scanners: null,
  top_method: null,
  top_response_code: null,
  top_region_detection: null,

  llmsigs: null,

  // filter
  events: null,
  total_events_count: 0,
  from: 0,
  rows_per_page: 5,
  filter: null,

  ///
  errMsg: null,
};

const ZoneReducer = (state, action) => {
  switch (action.type) {
    case GET_ZONES:
      return {
        ...state,
        zones: action.payload.zones,
      };
    case GET_ZONES_FOR_ITEMS:
      return {
        ...state,
        zoneList: action.payload.zoneList,
      };
    case GET_ZONES_FOR_LIST:
      return {
        ...state,
        zone_List: action.payload.zone_List,
      };
    case CONFIG_ZONE:
      return {
        ...state,
        curzone: action.payload.curzone,
      };
    case GET_REGIONAL_DETECTIONS:
      return {
        ...state,
        regional_detections: action.payload.regional_detections,
      };
    case GET_TOP_SOURCE:
      return {
        ...state,
        top_source_detection: action.payload.top_source_detection,
      };
    case GET_TOP_REGION:
      return {
        ...state,
        top_region_detection: action.payload.top_region_detection,
      };
    case GET_TOP_PATH:
      return {
        ...state,
        top_path: action.payload.top_path,
      };
    case GET_TOP_UA:
      return {
        ...state,
        top_ua: action.payload.top_ua,
      };
    case GET_TOP_PROMPT_SCANNERS:
      return {
        ...state,
        top_prompt_scanners: action.payload.top_prompt_scanners,
      };
    case GET_TOP_OUTPUT_SCANNERS:
      return {
        ...state,
        top_output_scanners: action.payload.top_output_scanners,
      };
    case GET_TOP_METHOD:
      return {
        ...state,
        top_method: action.payload.top_method,
      };
    case GET_TOP_RESPONSE_CODE:
      return {
        ...state,
        top_response_code: action.payload.top_response_code,
      };
    case GET_LLM_SIGS:
      return {
        ...state,
        llmsigs: action.payload.llmsigs,
      };
    case GET_EVENTS:
      return {
        ...state,
        events: action.payload.events,
      };
    case GET_TOTAL_EVENTS_COUNT:
      return {
        ...state,
        total_events_count: action.payload.total_events_count,
      };
    case SET_CURRENT_STATUS:
      return {
        ...state,
        rows_per_page: action.payload.rows_per_page,
        from: action.payload.from,
        filter: action.payload.filter,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const ZoneContext = createContext(null);

let eventController;

function ZoneProvider({ children }) {
  const { setAccessToken } = useAuth();
  const [state, dispatch] = useReducer(ZoneReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const getZones = useCallback(
    async (setZoneController) => {
      setErr(null);
      dispatch({
        type: GET_ZONES,
        payload: {
          zones: null,
        },
      });
      const controller = new AbortController();
      const signal = controller.signal;
      setZoneController(controller);
      try {
        const response = await axios.get("/zone", { signal });
        dispatch({
          type: GET_ZONES,
          payload: {
            zones: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_ZONES,
          payload: {
            zones: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getZonesForItems = useCallback(async () => {
    try {
      await setAccessToken();
      const response = await axios.get("/zone/basis");
      dispatch({
        type: GET_ZONES_FOR_ITEMS,
        payload: {
          zoneList: response.data,
        },
      });
    } catch (err) {
      dispatch({
        type: GET_ZONES_FOR_ITEMS,
        payload: {
          zoneList: [],
        },
      });
      setErr(err.message);
    }
  }, [setErr, setAccessToken]);
  const getZonesForList = useCallback(async () => {
    dispatch({
      type: GET_ZONES_FOR_LIST,
      payload: {
        zone_List: null,
      },
    });
    try {
      const response = await axios.get("/zone/basis_all");
      dispatch({
        type: GET_ZONES_FOR_LIST,
        payload: {
          zone_List: response.data,
        },
      });
    } catch (err) {
      dispatch({
        type: GET_ZONES_FOR_LIST,
        payload: {
          zone_List: [],
        },
      });
      setErr(err.message);
    }
  }, [setErr]);

  const createZone = useCallback(
    async (zoneID, zoneIP, zonePath) => {
      try {
        const response = await axios.post("/zone", {
          zone_id: zoneID,
          zone_addr: zoneIP,
          zone_name: zoneID,
          zone_path: zonePath,
          subdomains: [],
        });
        getZonesForItems();
        return { status: "success", msg: response.data };
      } catch (err) {
        return { status: "fail", msg: err.message };
      }
    },
    [getZonesForItems]
  );
  const settingApply = useCallback(async (zoneID, action = ConfigAction.ALL) => {
    try {
      const response = await axios.post("/zone/applyConfig", {
        zone_id: zoneID,
        action: action,
      });
      return { msg: response.data.msg, status: "success" };
    } catch (err) {
      let error_msg = err.message;
      return { msg: error_msg, status: "error" };
    }
  }, []);
  const onCreateSuccess = useCallback(async (zoneID) => {
    try {
      const response = await axios.post("/zone/onCreate", {
        zone_id: zoneID,
      });
      return { msg: response.data.msg, status: "success" };
    } catch (err) {
      let error_msg = err.message;
      return { msg: error_msg, status: "error" };
    }
  }, []);

  const updateZone = useCallback(
    async (zone_uid, values, subDomainList) => {
      const tmpDomains = [];
      for (let index = 0; index < subDomainList.length; index++) {
        if (subDomainList[index].name === "") continue;
        tmpDomains.push(subDomainList[index]);
      }
      values.subdomains = tmpDomains;
      /*
        try {
            await axios.post("/zone", values);
            getZonesForItems();
        } catch (err) {
            setErr(err.message);
        }
        */
      await axios.put(`/zone/${zone_uid}`, values);
      getZonesForItems();
    },
    [getZonesForItems]
  );

  const deleteZone = useCallback(
    async (zoneID, removeFlag) => {
      try {
        if (removeFlag) {
          await axios.delete("/zone", {
            data: { zone_id: zoneID },
          });
        } else {
          await axios.patch("/zone/bulkDelete", {
            zone_id: zoneID,
            deleted: true,
          });
        }
        getZonesForItems();
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getZonesForItems]
  );
  const restoreZone = useCallback(
    async (zoneID) => {
      try {
        await axios.patch("/zone/bulkDelete", {
          zone_id: zoneID,
          deleted: false,
        });
        getZonesForItems();
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getZonesForItems]
  );

  const viewZone = useCallback(async (zoneID) => {
    const response = await axios.get(`/zone/${zoneID}`);
    return response.data;
  }, []);
  const selectZone = useCallback(
    async (zoneID) => {
      if (zoneID === "all") {
        dispatch({
          type: CONFIG_ZONE,
          payload: {
            curzone: { zone_id: "all" },
          },
        });
        dispatch({
          type: SET_ERROR,
          payload: {
            errMsg: null,
          },
        });
      } else {
        try {
          if (state.curzone?.zone_id === zoneID) {
            return state.curzone;
          }
          const response = await axios.get(`/zone`, {
            params: { zone_id: zoneID },
          });
          dispatch({
            type: CONFIG_ZONE,
            payload: {
              curzone: response.data,
            },
          });
          dispatch({
            type: SET_ERROR,
            payload: {
              errMsg: null,
            },
          });
          return response.data;
        } catch (err) {
          dispatch({
            type: CONFIG_ZONE,
            payload: {
              curzone: null,
            },
          });
          setErr(err.message);
        }
      }
    },
    [setErr, state.curzone]
  );
  // const changeZone = (zoneID, ruleID) => {
  //     axios.get(`/zone/${zoneID}`).then((response) => {
  //         const curzone = response.data;
  //         dispatch({
  //             type: CONFIG_ZONE,
  //             payload: {
  //                 curzone: curzone,
  //             },
  //         });
  //         window.localStorage.setItem("curzone", zoneID);
  //     });

  //     axios
  //         .post("/config/waf/get_crs_rule", {
  //             zone_id: zoneID,
  //             rule_id: ruleID,
  //         })
  //         .then((response) => {
  //             const currule = response.data;
  //             dispatch({
  //                 type: SELECT_RULE,
  //                 payload: {
  //                     currule: currule,
  //                 },
  //             });
  //         });
  // };
  const getRegionalDetections = useCallback(
    async (zone_id, time_range, signal) => {
      dispatch({
        type: GET_REGIONAL_DETECTIONS,
        payload: {
          regional_detections: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/regional_detection",
          {
            zone_id: zone_id,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopSource = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_SOURCE,
        payload: {
          top_source_detection: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_source_detection",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source_detection: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source_detection: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopRegion = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_REGION,
        payload: {
          top_region_detection: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_region_detection",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_REGION,
          payload: {
            top_region_detection: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_REGION,
          payload: {
            top_region_detection: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopPath = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_PATH,
        payload: {
          top_path: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_path",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopUA = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_UA,
        payload: {
          top_ua: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_ua",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopPromptScanners = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_PROMPT_SCANNERS,
        payload: {
          top_prompt_scanners: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_prompt_scanners",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_PROMPT_SCANNERS,
          payload: {
            top_prompt_scanners: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_PROMPT_SCANNERS,
          payload: {
            top_prompt_scanners: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopOutputScanners = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_OUTPUT_SCANNERS,
        payload: {
          top_output_scanners: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_output_scanners",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_OUTPUT_SCANNERS,
          payload: {
            top_output_scanners: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_OUTPUT_SCANNERS,
          payload: {
            top_output_scanners: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopMethod = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_METHOD,
        payload: {
          top_method: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_method",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getTopResponseCode = useCallback(
    async (zone_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_RESPONSE_CODE,
        payload: {
          top_response_code: null,
        },
      });
      try {
        const response = await axios.post(
          "llm_stats/top_res_code_detection",
          {
            zone_id: zone_id,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getLLMSigs = useCallback(
    async (zone_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "llm_stats/detection",
          {
            zone_id: zone_id,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_LLM_SIGS,
          payload: {
            llmsigs: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LLM_SIGS,
          payload: {
            llmsigs: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getDashboardInfo = useCallback(
    (setLLMDashController, zone_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMDashController(controller);

      getRegionalDetections(zone_id, time_range, signal);
      getTopRegion(zone_id, time_range, 5, signal);
      getTopSource(zone_id, time_range, 5, signal);
      getTopPath(zone_id, time_range, 5, signal);
      getTopUA(zone_id, time_range, 5, signal);
      getTopPromptScanners(zone_id, time_range, 5, signal);
      getTopOutputScanners(zone_id, time_range, 5, signal);
      getLLMSigs(zone_id, time_range, signal);
    },
    [
      getRegionalDetections,
      getTopRegion,
      getTopSource,
      getTopPath,
      getTopUA,
      getTopPromptScanners,
      getTopOutputScanners,
      getLLMSigs,
    ]
  );
  const getTopEventsInfo = useCallback(
    (setLLMEventController, zone_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setLLMEventController(controller);

      getTopRegion(zone_id, time_range, undefined, signal);
      getTopSource(zone_id, time_range, undefined, signal);
      getTopPath(zone_id, time_range, undefined, signal);
      getTopUA(zone_id, time_range, undefined, signal);
      getTopPromptScanners(zone_id, time_range, undefined, signal);
      getTopOutputScanners(zone_id, time_range, undefined, signal);
      getTopMethod(zone_id, time_range, undefined, signal);
      getTopResponseCode(zone_id, time_range, undefined, signal);
    },
    [getTopRegion, getTopSource, getTopPath, getTopUA, getTopPromptScanners, getTopOutputScanners, getTopMethod, getTopResponseCode]
  );
  const getOnlyEvents = useCallback(
    async (zone_id, time_range, rows_per_page, from, filter) => {
      dispatch({
        type: GET_EVENTS,
        payload: {
          events: null,
        },
      });
      dispatch({
        type: SET_CURRENT_STATUS,
        payload: {
          rows_per_page: rows_per_page,
          from: from,
          filter: filter,
        },
      });
      const value = {
        zone_id: zone_id,
        time_range: time_range,
        count: rows_per_page,
        from: from,
        conditions: filter,
      };

      if (eventController) eventController.abort();
      eventController = new AbortController();
      const signal = eventController.signal;
      try {
        const response = await axios.post("llm_log/llm_event", value, {
          signal,
        });
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const setTotalCountToZero = useCallback(() => {
    dispatch({
      type: GET_TOTAL_EVENTS_COUNT,
      payload: {
        total_events_count: 0,
      },
    });
  }, []);

  return (
    <ZoneContext.Provider
      value={{
        ...state,
        getZones,
        getZonesForItems,
        getZonesForList,
        createZone,
        settingApply,
        onCreateSuccess,
        updateZone,
        deleteZone,
        restoreZone,
        viewZone,
        selectZone,
        getRegionalDetections,
        getTopSource,
        getTopRegion,
        getTopPath,
        getTopUA,
        getTopPromptScanners,
        getTopOutputScanners,
        getTopMethod,
        getTopResponseCode,
        getLLMSigs,
        getDashboardInfo,
        getTopEventsInfo,
        getOnlyEvents,
        setTotalCountToZero,
        setErr,
      }}
    >
      {children}
    </ZoneContext.Provider>
  );
}

export { ZoneContext, ZoneProvider };
